import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Volume2, ChevronRight, Trophy } from "lucide-react";
import { api, speak } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { WordIcon } from "@/components/WordIcon";
import { PronounceButton } from "@/components/PronounceButton";
import { useTranslations, Translation } from "@/lib/translations";

export default function PronunciationPage() {
  const [words, setWords] = useState(null);
  const [idx, setIdx] = useState(0);
  const [ok, setOk] = useState(0);
  const [answered, setAnswered] = useState(false);
  useEffect(() => { api.get("/vocabulary").then((r) => setWords(r.data.slice(0, 8))); }, []);
  const w = words?.[idx];
  const { map, rtl } = useTranslations(w ? [w] : []);

  const back = (
    <Link to="/vocabulaire" data-testid="pron-back-btn" className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900">
      <ArrowLeft className="w-5 h-5" /> Mes mots
    </Link>
  );
  if (!words) return <Skeleton className="h-64 w-full" />;
  if (!words.length) return <div className="space-y-4">{back}<p className="text-slate-600" data-testid="pron-empty">Terminez une leçon pour vous entraîner.</p></div>;
  if (idx >= words.length)
    return (
      <div className="text-center space-y-5 py-8 rise" data-testid="pron-result">
        <Trophy className="w-14 h-14 mx-auto text-amber-500" />
        <p className="font-display text-6xl font-extrabold" data-testid="pron-score">{ok}/{words.length}</p>
        <p className="text-slate-600">mots bien compris</p>
        <button onClick={() => { setIdx(0); setOk(0); setAnswered(false); }} data-testid="pron-restart-btn" className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display text-xl font-bold uppercase transition-colors">Recommencer</button>
        {back}
      </div>
    );

  return (
    <div className="space-y-5" data-testid="pron-page">
      <div className="flex items-center justify-between">
        {back}
        <span className="font-bold text-slate-600" data-testid="pron-counter">{idx + 1} / {words.length}</span>
      </div>
      <h1 className="font-display text-3xl font-extrabold uppercase">Répétez le mot</h1>
      <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 flex flex-col items-center gap-3 text-center">
        <WordIcon word={w} size="lg" />
        <p className="font-display text-4xl font-bold" data-testid="pron-word">{w.word}</p>
        <Translation text={map[w.word]} rtl={rtl} testId="pron-translation" />
        <button onClick={() => speak(w.word, 0.85)} data-testid="pron-listen-btn" className="h-12 px-5 rounded-xl bg-slate-900 text-amber-400 hover:bg-slate-800 font-bold flex items-center gap-2 transition-colors">
          <Volume2 className="w-5 h-5" /> Écouter d'abord
        </button>
      </div>
      <PronounceButton key={w.word} word={w.word} big testId="pron-mic-btn" onResult={(r) => { if (!answered && r.ok) setOk((n) => n + 1); if (!r.error) setAnswered(true); }} />
      <button
        onClick={() => { setIdx(idx + 1); setAnswered(false); }}
        data-testid="pron-next-btn"
        className="w-full h-14 rounded-xl border-2 border-slate-900 font-display text-xl font-bold uppercase flex items-center justify-center gap-2 hover:bg-slate-900 hover:text-white transition-colors"
      >
        {answered ? "Mot suivant" : "Passer"} <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}
